import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, type HttpEvent, type HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ApiUrlService } from '@app/services/api-url.service';

/**
 * Sends the active UI language as `locale` on every API request. The backend's `set_locale`
 * reads `params[:locale]` first, so without it server-side messages (validation errors, the
 * RFID storing lock, ...) come back in the session/browser language instead of the one the
 * user is looking at.
 */
@Injectable()
export class LocaleInterceptor implements HttpInterceptor {
  // TranslateService is resolved lazily: it pulls in the HTTP translation loader, so injecting
  // it in the constructor would be a cyclic dependency with HttpClient.
  constructor(private injector: Injector, private apiUrlService: ApiUrlService) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    if (!this.isApiRequest(request.url) || /[?&]locale=/.test(request.urlWithParams)) {
      return next.handle(request);
    }

    const locale = this.currentLocale;
    return next.handle(locale ? request.clone({ setParams: { locale } }) : request);
  }

  private isApiRequest(url: string): boolean {
    const apiUrl = this.apiUrlService.apiUrl;
    return !!apiUrl && url.startsWith(apiUrl) && url.includes('/api/');
  }

  private get currentLocale(): string | null {
    const translate = this.injector.get(TranslateService, null);
    return translate?.getCurrentLang() || translate?.getFallbackLang() || null;
  }
}
